import type { VariantProps } from "class-variance-authority";
import * as React from "react";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { cva } from "class-variance-authority";
import { X } from "lucide-react";

import { cn } from "~/lib/ui/utils/utils";

const Sheet = SheetPrimitive.Root;

const SheetTrigger = SheetPrimitive.Trigger;

const SheetPortal = SheetPrimitive.Portal;

function SheetOverlay({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay>) {
  return (
    <SheetPrimitive.Overlay
      className={cn(
        `
          fixed inset-0 z-50 bg-black/60 backdrop-blur-sm

          data-[state=closed]:animate-out data-[state=closed]:fade-out-0

          data-[state=open]:animate-in data-[state=open]:fade-in-0
        `,
        className,
      )}
      {...props}
    />
  );
}

const sheetVariants = cva(
  `
    fixed z-50 max-h-dvh gap-4 overflow-y-auto bg-background p-6 shadow-lg
    outline-hidden transition
    ease-in-out border-subtle

    data-[state=closed]:duration-150 data-[state=closed]:animate-out

    data-[state=open]:duration-150 data-[state=open]:animate-in
  `,
  {
    defaultVariants: {
      side: "right",
    },
    variants: {
      side: {
        bottom: cn(`
          inset-x-0 bottom-0 border-t

          data-[state=closed]:slide-out-to-bottom

          data-[state=open]:slide-in-from-bottom
        `),
        left: cn(`
          inset-y-0 left-0 h-dvh w-3/4 border-r

          data-[state=closed]:slide-out-to-left

          data-[state=open]:slide-in-from-left

          sm:max-w-sm
        `),
        right: cn(`
          inset-y-0 right-0 h-dvh w-3/4 border-l

          data-[state=closed]:slide-out-to-right

          data-[state=open]:slide-in-from-right

          sm:max-w-sm
        `),
        top: cn(`
          inset-x-0 top-0 border-b

          data-[state=closed]:slide-out-to-top

          data-[state=open]:slide-in-from-top
        `),
      },
    },
  },
);

type SheetContentProps = React.ComponentProps<typeof SheetPrimitive.Content> & {
  closeButtonAriaLabel?: string;
  showCloseButton?: boolean;
} & VariantProps<typeof sheetVariants>;

function SheetContent({
  children,
  className,
  closeButtonAriaLabel = "Close Side Panel",
  showCloseButton = true,
  side = "right",
  ...props
}: SheetContentProps) {
  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Content
        onOpenAutoFocus={(e) => e.preventDefault()}
        className={cn(sheetVariants({ side }), className)}
        {...props}
      >
        {children}
        {showCloseButton ? (
          <SheetPrimitive.Close
            aria-label={closeButtonAriaLabel}
            className={`
              absolute right-3 top-3 z-10 h-8 w-8 rounded-md
              bg-secondary opacity-70
              ring-offset-background transition-opacity

              data-[state=open]:bg-secondary

              disabled:pointer-events-none

              focus-visible:outline-hidden focus-visible:ring-2
              focus-visible:ring-ring focus-visible:ring-offset-2

              hover:opacity-100
            `}
          >
            <X className="m-auto h-4 w-4" />
            <span className="sr-only">{closeButtonAriaLabel}</span>
          </SheetPrimitive.Close>
        ) : null}
      </SheetPrimitive.Content>
    </SheetPortal>
  );
}

const SheetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      `
        flex flex-col space-y-2 text-center

        sm:text-left
      `,
      className,
    )}
    {...props}
  />
);
SheetHeader.displayName = "SheetHeader";

function SheetTitle({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return <SheetPrimitive.Title className={cn(className)} {...props} />;
}

function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  );
}

export {
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
};
